import { z } from "zod";

import { parseBrCivilDate } from "@/lib/civil-date";
import { taxPayerIdSchema } from "@/lib/cpf";
import { buildEventStepSchema } from "@/lib/create-list/create-list";
import {
  eventLocationField,
  eventNameFieldsSchema,
  welcomeMessageField,
} from "@/lib/event-form/event-form";
import { parseCatalogQuery, type CatalogQuery } from "@/lib/host-panel/catalog-query";
import { addressStepSchema, onboardingSignupSchema } from "@/lib/onboarding/onboarding-schemas";

import { NOVA_LISTA_COPY } from "./nova-lista-copy";

/**
 * Zod schemas of the assisted list creation (`/admin/nova-lista`): the Staff_Wizard's forms
 * (customer data, event, store and administrator) and the Staff_BFF's bodies, queries and route
 * params (design §3), so the browser and the BFF validate with the same rules (Req. 12.5).
 *
 * Wherever the host already has a rule, the rule is the host's (Req. 13.1): the customer fields
 * are a `.pick` of `onboardingSignupSchema`, the event date floor is the date schema of
 * `lib/create-list`, the address is `addressStepSchema` and the CPF is `taxPayerIdSchema`. Only
 * the words change, to the staff's (`NOVA_LISTA_COPY`). No host file is touched.
 */

const DATA_ERRORS = NOVA_LISTA_COPY.data.errors;
const EVENT_ERRORS = NOVA_LISTA_COPY.event.errors;
const PICKER_ERRORS = NOVA_LISTA_COPY.picker.errors;



/** Same bound as the host's own suggestion body (`/api/host/nova-lista/sugestao`). */
const MAX_SUGGESTION_SLUG_LENGTH = 120;

// ─── Shared pieces ───────────────────────────────────────────────────────────

/**
 * Runs a host field's rule (its trim, its digit mask, its bounds) and reports any failure with one
 * staff message. The host schema hard-codes its own words, and a schema-level message wins over
 * any error map, so the issue is replaced here instead of the host checks being rewritten. What
 * comes out is the host field's output (e.g. the phone reduced to its digits).
 */
function overrideMessage<TOutput>(field: z.ZodType<TOutput, string>, message: string) {
  return z.string(message).transform((value, ctx): TOutput => {
    const result = field.safeParse(value);
    if (result.success) return result.data;
    ctx.issues.push({ code: "custom", message, input: value });
    return z.NEVER;
  });
}



/** A local id (`@db.Uuid` in the backend), as the Staff_BFF receives it in params, queries and bodies. */
export const uuidSchema = z.string().uuid();

// ─── Customer data (Req. 5.1, 5.4) ───────────────────────────────────────────

/**
 * The host signup's contact fields with their rules untouched: names trimmed with at least two
 * characters, the phone reduced to its digits (10 or 11, with DDD) and the e-mail format.
 */
const hostContactFields = onboardingSignupSchema.pick({
  firstName: true,
  lastName: true,
  phone: true,
  email: true,
}).shape;

/**
 * The data step of SignupStepForm `variant="staff"`: name, surname, mobile and e-mail with the
 * staff messages. No birth date, gender or marketing opt-in.
 */
export const staffCustomerDataSchema = z.object({
  firstName: overrideMessage(hostContactFields.firstName, DATA_ERRORS.firstName),
  lastName: overrideMessage(hostContactFields.lastName, DATA_ERRORS.lastName),
  phone: overrideMessage(hostContactFields.phone, DATA_ERRORS.phone),
  email: overrideMessage(hostContactFields.email, DATA_ERRORS.email),
});
export type StaffCustomerDataValues = z.infer<typeof staffCustomerDataSchema>;

// ─── Event (Req. 8.5, 8.6) ───────────────────────────────────────────────────

/**
 * The host's event date with its floor: a date being chosen must be from tomorrow in Brazil, in
 * the host's words (`CREATE_LIST_MESSAGE.dateInPast`). Built without a saved date because the
 * list does not exist yet, so there is no told date to keep.
 */
const hostEventDateField = buildEventStepSchema().shape.eventDate;

/**
 * `DD/MM/YYYY`. Blank, or not a calendar date, reads the staff message; a real date then goes
 * through the host's field, so the only issue left for it to report is the floor.
 */
const eventDateField = z
  .string(EVENT_ERRORS.eventDate)
  .trim()
  .refine((value) => parseBrCivilDate(value) !== null, EVENT_ERRORS.eventDate)
  .pipe(hostEventDateField);

/**
 * The InternalEventStep: who receives, the other party, the welcome message, the date and the
 * place. No guest count (Req. 8.7).
 */
export const internalEventSchema = z.object({
  /*
   * The four name parts, the welcome message and the place now come from the shared event form
   * (`lib/event-form`), which was seeded from this very file — same messages, same rules. What
   * changes for this flow is one fix it was missing: the message and the place had no ceiling,
   * while the column caps them at 240 and 255 and the backend answers 400 above that.
   */
  ...eventNameFieldsSchema(),
  welcomeMessage: welcomeMessageField(),
  eventDate: eventDateField,
  eventLocation: eventLocationField(),
});
export type InternalEventValues = z.infer<typeof internalEventSchema>;

// ─── Store and administrator (Req. 7.5) ──────────────────────────────────────

/** The StoreAdministratorPicker: both choices are required on the event submit. */
export const storeAdministratorSchema = z.object({
  locationId: z.string(PICKER_ERRORS.store).uuid(PICKER_ERRORS.store),
  administratorId: z.string(PICKER_ERRORS.administrator).uuid(PICKER_ERRORS.administrator),
});
export type StoreAdministratorValues = z.infer<typeof storeAdministratorSchema>;

/**
 * The InternalEventStep's form: the event and the StoreAdministratorPicker's two choices in one
 * schema, so one submit validates both and each message lands on its own field (Req. 7.5, 8.5).
 */
export const internalEventFormSchema = internalEventSchema.extend(storeAdministratorSchema.shape);
/** What the form holds while it is filled in: every field a string, possibly empty. */
export type InternalEventFormInput = z.input<typeof internalEventFormSchema>;
export type InternalEventFormValues = z.infer<typeof internalEventFormSchema>;

// ─── Staff_BFF bodies, queries and params (design §3, Req. 12.5) ─────────────

/** `GET customers?cpf=`: eleven digits with a valid checksum (Req. 3.6). */
export const customerLookupQuerySchema = z.object({ cpf: taxPayerIdSchema });
export type CustomerLookupQuery = z.infer<typeof customerLookupQuerySchema>;

/**
 * `POST customers`: the confirmed data, the looked-up CPF and the address form's values, which
 * the BFF maps with `toBackendAddress` into the backend's `addresses` (at least one, Req. 6.4).
 */
export const createCustomerBodySchema = staffCustomerDataSchema.extend({
  cpf: taxPayerIdSchema,
  address: addressStepSchema,
});
export type CreateCustomerBody = z.infer<typeof createCustomerBodySchema>;

/**
 * `PATCH customers/[customerId]`: any subset of name, surname and mobile. The e-mail is not part
 * of the schema, so it is stripped and never reaches the backend (Req. 5.6).
 */
export const patchCustomerBodySchema = staffCustomerDataSchema
  .pick({ firstName: true, lastName: true, phone: true })
  .partial();
export type PatchCustomerBody = z.infer<typeof patchCustomerBodySchema>;

/** `POST customers/[customerId]/addresses`: the address form's values, mapped by `toBackendAddress` (Req. 6.5). */
export const customerAddressBodySchema = addressStepSchema;
export type CustomerAddressBody = z.infer<typeof customerAddressBodySchema>;

/** `GET users?locationId=`: the store whose users can administer the list (Req. 7.3, 7.4). */
export const usersQuerySchema = z.object({ locationId: uuidSchema });
export type UsersQuery = z.infer<typeof usersQuerySchema>;

/**
 * `POST lists`: the event, the store, the chosen administrator (the backend's `sellerId`,
 * Req. 8.8) and the Customer who owns the list (`ownerId`).
 */
export const createListBodySchema = internalEventSchema
  .extend(storeAdministratorSchema.shape)
  .extend({ ownerId: uuidSchema });
export type CreateListBody = z.infer<typeof createListBodySchema>;

/** `POST lists/[listId]/suggestion`: the chosen suggestion's slug (Req. 10.5). */
export const suggestionBodySchema = z.object({
  slug: z.string().trim().min(1).max(MAX_SUGGESTION_SLUG_LENGTH),
});
export type SuggestionBody = z.infer<typeof suggestionBodySchema>;

/** `POST lists/[listId]/handoff`: `resend: true` sends the communication again (Req. 11.7). */
export const handoffBodySchema = z.object({ resend: z.boolean().optional() });
export type HandoffBody = z.infer<typeof handoffBodySchema>;

/** The `[listId]` route segment, for the Staff_BFF routes and the `[listId]` pages. */
export const listIdParamsSchema = z.object({ listId: uuidSchema });
export type ListIdParams = z.infer<typeof listIdParamsSchema>;

/** The `[customerId]` route segment. */
export const customerIdParamsSchema = z.object({ customerId: uuidSchema });
export type CustomerIdParams = z.infer<typeof customerIdParamsSchema>;

/** A query string as `withStaffRoute` reads it: a name given once is a string, a repeated name an array. */
const rawQuerySchema = z.record(z.string(), z.union([z.string(), z.array(z.string())]));

/** Back to `URLSearchParams`, values of a repeated name in their original order. */
function toSearchParams(query: z.infer<typeof rawQuerySchema>): URLSearchParams {
  const params = new URLSearchParams();
  for (const [name, value] of Object.entries(query)) {
    for (const item of typeof value === "string" ? [value] : value) params.append(name, item);
  }

  return params;
}

/**
 * `GET lists/[listId]/catalog`: the catalog page's filters, read by the host's own
 * `parseCatalogQuery`, exactly as `/api/host/catalog` reads them. Lenient like the host's route:
 * an unreadable value falls back to its default instead of failing, so no query is a 400.
 */
export const catalogPageQuerySchema = rawQuerySchema.transform(
  (query): CatalogQuery => parseCatalogQuery(toSearchParams(query)),
);
export type CatalogPageQuery = z.infer<typeof catalogPageQuerySchema>;
